import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Phone, MessageSquare, CheckCircle, AlertTriangle, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { businessInfo } from '../data/mock';

const SpringReplacementPage = () => {
  const springTypes = [
    {
      title: "High-Cycle Torsion Springs",
      cycles: "25,000+ cycles",
      desc: "Premium springs that last 2-3x longer than standard springs. Mounted above the door for balanced operation.",
      features: ["Superior durability", "Quieter operation", "Better balance", "Galvanized steel construction"]
    },
    {
      title: "Standard Torsion Springs",
      cycles: "10,000-15,000 cycles",
      desc: "Cost-effective option for Saskatoon homeowners. Reliable performance for normal use.",
      features: ["Good value", "Reliable operation", "Standard warranty", "Professional installation"]
    }
  ];

  const warningSigns = [
    "Loud bang or snapping sound",
    "Door won't open or is very heavy",
    "Visible gap or break in spring",
    "Door closes too fast",
    "Door hangs crooked or uneven",
    "Garage door opener straining"
  ];

  const safetyInfo = [
    "Springs under 300+ pounds of tension",
    "DIY replacement extremely dangerous",
    "Professional tools required",
    "Risk of serious injury or death",
    "Requires specialized training",
    "Insurance may not cover DIY injuries"
  ];

  const warrantyInfo = [
    "Lifetime warranty available on high-cycle springs",
    "Standard warranty on all spring replacements",
    "Labor warranty on installation workmanship",
    "Galvanized steel for rust resistance",
    "Free follow-up adjustment if needed"
  ];

  const faqItems = [
    {
      q: "How long do garage door springs last in Saskatoon?",
      a: "Standard springs last 7-10 years or 10,000-15,000 cycles. High-cycle springs can last 15+ years or 25,000+ cycles. Saskatoon's temperature fluctuations can affect spring life. We recommend high-cycle galvanized steel springs for maximum durability in our climate."
    },
    {
      q: "Why do both springs need to be replaced?",
      a: "When one spring breaks, the other is at the same wear level and will likely break soon. Replacing both springs ensures balanced operation and prevents another service call in weeks. It's more cost-effective for Saskatoon homeowners to replace both at once."
    },
    {
      q: "What's the difference between standard and high-cycle springs?",
      a: "Standard springs (10,000-15,000 cycles) last about 7-10 years. High-cycle springs (25,000+ cycles) last 2-3x longer - often 15+ years. High-cycle springs cost more initially but save money long-term for Saskatoon homeowners. Both types use galvanized steel construction."
    },
    {
      q: "Do you offer warranty on spring replacement?",
      a: "Yes! We offer lifetime warranty options on high-cycle springs and standard warranties on all spring replacements. Our labor warranty covers installation workmanship. All springs are galvanized steel for rust resistance in Saskatoon's climate."
    },
    {
      q: "Can I replace garage door springs myself?",
      a: "We strongly advise against DIY spring replacement. Springs are under extreme tension (300+ pounds) and can cause severe injury or death. Professional technicians have specialized tools and training. Insurance may not cover DIY injuries. For Saskatoon families' safety, always use professional service."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Garage Door Spring Replacement Saskatoon
            </h1>
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              Professional spring replacement using high-cycle galvanized steel springs. Same-day service available for Saskatoon homeowners. Lifetime warranty options on premium springs.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button onClick={() => window.location.href = `tel:${businessInfo.phone}`} size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-10 py-6 text-lg">
                <Phone size={22} className="mr-2" />Call {businessInfo.phone}
              </Button>
              <Button onClick={() => window.location.href = '/quote'} size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-10 py-6 text-lg">
                <MessageSquare size={22} className="mr-2" />Get Free Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="bg-red-50 border-l-4 border-red-600 p-8 mb-12">
              <div className="flex items-start gap-4">
                <AlertTriangle size={48} className="text-red-600 flex-shrink-0" />
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Broken Spring? This is Dangerous</h2>
                  <p className="text-gray-700 text-lg mb-4">
                    A broken garage door spring makes your door unsafe to operate. The spring carries the door's entire weight - typically 150-300 pounds. Without functioning springs, your door won't open properly and could crash down unexpectedly, risking injury to Saskatoon families and damage to vehicles.
                  </p>
                  <p className="text-gray-700 text-lg">
                    At 306 Garage Doors, we provide same-day spring replacement service throughout Saskatoon. We use high-cycle galvanized steel springs rated for 25,000+ cycles - lasting 2-3 times longer than standard springs. All work includes warranty protection and professional installation.
                  </p>
                </div>
              </div>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">High-Cycle Springs: Superior Quality</h2>
              <p>
                Not all garage door springs are created equal. At 306 Garage Doors, we specialize in high-cycle spring installation for Saskatoon homes and businesses. Here's what makes our springs superior:
              </p>
              
              <h3 className="text-2xl font-bold text-gray-900">High-Cycle Spring Benefits</h3>
              <ul className="list-disc pl-6 space-y-3">
                <li><strong>25,000+ Cycle Rating:</strong> Our premium springs last 2-3 times longer than standard 10,000-cycle springs</li>
                <li><strong>Galvanized Steel Construction:</strong> Rust-resistant coating protects against Saskatoon's temperature and humidity changes</li>
                <li><strong>Lifetime Warranty Available:</strong> We offer lifetime warranty options on high-cycle springs for ultimate peace of mind</li>
                <li><strong>Quieter Operation:</strong> Premium springs reduce noise and vibration during door operation</li>
                <li><strong>Better Balance:</strong> High-quality springs maintain proper door balance longer</li>
                <li><strong>Cost-Effective Long-Term:</strong> While more expensive initially, they save money over time for Saskatoon homeowners</li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-900 mt-8">Residential & Commercial Spring Service</h3>
              <p>
                We serve both residential and commercial properties in Saskatoon:
              </p>
              <p>
                <strong>Residential Spring Replacement:</strong> For Saskatoon homes, we replace torsion springs and extension springs on single and double garage doors. We stock springs for standard door sizes and can order custom springs for unusual door weights or sizes. Same-day service available.
              </p>
              <p>
                <strong>Commercial Spring Service:</strong> Commercial doors require heavy-duty springs rated for frequent use. We service rolling steel doors, high-speed doors, and large commercial overhead doors throughout Saskatoon. Our commercial springs use commercial-grade galvanized steel for maximum durability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spring Types */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Spring Options for Saskatoon Properties</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {springTypes.map((type, idx) => (
                <Card key={idx} className="border-2 border-blue-600">
                  <CardHeader>
                    <div className="bg-blue-600 text-white px-4 py-2 rounded text-center font-bold text-lg mb-3">
                      {type.cycles}
                    </div>
                    <CardTitle className="text-2xl text-gray-900">{type.title}</CardTitle>
                    <p className="text-gray-600 mt-2">{type.desc}</p>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {type.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-start gap-2">
                          <CheckCircle size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Warning Signs */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Warning Signs Your Springs Need Replacement</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {warningSigns.map((sign, idx) => (
                <Card key={idx} className="border-2 border-orange-200">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={20} className="text-orange-600 flex-shrink-0 mt-1" />
                      <p className="text-gray-800 font-semibold">{sign}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Safety Warning */}
      <section className="py-16 bg-red-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <Card className="border-4 border-red-600">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <AlertTriangle size={64} className="text-red-600 flex-shrink-0" />
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">DANGER: Never Attempt DIY Spring Replacement</h2>
                    <p className="text-gray-700 text-lg mb-4">
                      Garage door springs are under extreme tension and are one of the most dangerous components to work on. Professional training and specialized tools are absolutely required.
                    </p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {safetyInfo.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-1" />
                      <p className="text-gray-800 font-semibold">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Warranty */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Spring Replacement Warranty</h2>
            <Card className="border-2 border-blue-600">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <Shield size={48} className="text-blue-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Comprehensive Warranty Coverage</h3>
                    <p className="text-gray-700 text-lg mb-4">
                      All spring replacement work by 306 Garage Doors includes warranty protection. We stand behind our work and the quality of our galvanized steel springs.
                    </p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {warrantyInfo.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-1" />
                      <p className="text-gray-800 font-semibold">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqItems.map((item, idx) => (
                <Card key={idx} className="border-2">
                  <CardHeader>
                    <CardTitle className="text-xl text-gray-900">{item.q}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 leading-relaxed">{item.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-600">
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Need Spring Replacement in Saskatoon?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Don't risk your family's safety with broken springs. Contact 306 Garage Doors for professional spring replacement using high-cycle galvanized steel springs with warranty protection.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={() => window.location.href = `tel:${businessInfo.phone}`} size="lg" className="bg-white text-blue-600 hover:bg-gray-100 font-bold px-12 py-8 text-xl">
              <Phone size={24} className="mr-2" />Call {businessInfo.phone}
            </Button>
            <Button onClick={() => window.location.href = '/quote'} size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-12 py-8 text-xl">
              Request Free Quote
            </Button>
          </div>
          <p className="mt-6 text-blue-100 italic">{businessInfo.confidentialMessage}</p>
        </div>
      </section>
    </div>
  );
};

export default SpringReplacementPage;